import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link, Outlet } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { WorkspaceProvider } from "@/contexts/workspace-context";
import { AppLink } from "@/components/app-link";
import {
  Sparkles,
  Settings,
  LogOut,
  Menu,
  ChevronsLeft,
  ChevronsRight,
  Layout,
  FolderOpen,
  BookOpen,
  FileText,
  LayoutPanelLeft,
  Plug,
  Grid3X3,
  X,
  Zap,
  Shield,
  Megaphone,
} from "lucide-react";
import { billingApi } from "@/lib/api/billing";
import { PlanType, SubscriptionDto } from "@optidev/shared-types";
import { BillingWarningBanner } from "@/components/billing/BillingWarningBanner";
import { PmHandoverBar } from "@/components/billing/pm-handover-bar";
import { PageBar, PageBarProvider } from "@/components/layout/page-bar";
import { ThemeSwitcherPopover } from "@/components/ui/theme-switcher-popover";
import { Hint } from "@/components/ui/hint";
import { UnsavedChangesDialog } from "@/components/visual-editor/unsaved-changes-dialog";
import { useVisualEditorHistoryStore } from "@/stores/visualEditorHistoryStore";
import optidevIcon from "@/assets/optidev-icon.svg";
import optidevHorizontal from "@/assets/optidev-horizontal.svg";
import optidevHorizontalWhite from "@/assets/optidev-horizontal-white.svg";
import { useResponsive } from "@/hooks/useResponsive";
import { useTheme } from "@/contexts/theme-context";
import { useEmbedStore } from "@/stores/embed-store";
import { useFeatures } from "@/hooks/useFeatures";
import { usePlatformAdmin } from "@/hooks/use-platform-admin";
import { WorkspaceSelector } from "@/components/workspace-selector";
// Headless: no longer renders a checklist. It still fetches the activation
// markers, which backfills the timestamps the activation funnels read, and
// supplies the first-edit mark used by `first_project_edit`.
import { OnboardingProvider } from "@/contexts/onboarding-context";
import { MaintenanceBanner } from "@/components/maintenance-banner";
import { AnnouncementBanner } from "@/components/announcement-banner";
import WorkspaceRecoveryBanner from "@/components/editor/power/WorkspaceRecoveryBanner";
import { ReferralBonusModal } from "@/components/referral-bonus-modal";
import { RemixIntentHandler } from "@/components/remix-intent-handler";
import { ContactSupportNavItem } from "@/components/support/contact-support-nav-item";

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  external?: boolean;
  featureKey?: "templates" | "connectors";
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    label: "Build",
    items: [
      { name: "AI Builder", href: "/dashboard", icon: Sparkles },
      { name: "Projects", href: "/dashboard/editor", icon: FolderOpen },
    ],
  },
  {
    label: "Integrations",
    items: [
      { name: "Connectors", href: "/dashboard/connectors", icon: Plug, featureKey: "connectors" as const },
      { name: "Skills", href: "/dashboard/skills", icon: LayoutPanelLeft },
    ],
  },
  {
    label: "Resources",
    items: [
      { name: "App Library", href: "/dashboard/apps", icon: Grid3X3 },
      { name: "Prompt Library", href: "/dashboard/prompt-library", icon: BookOpen },
      { name: "Documentation", href: "https://docs.optidev.ai", icon: FileText, external: true },
    ],
  },
];

export default function DashboardLayoutFull() {
  const { user, loading, signOut, getIdToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [planType, setPlanType] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDto | null>(null);

  // Check if we're in editor mode
  const isEditorMode =
    pathname.includes("/editor/") && pathname.includes("/power");

  // Unsaved visual editor changes guard for sidebar navigation
  const visualEditorHistory = useVisualEditorHistoryStore((s) => s.history);
  const clearVisualEditorHistory = useVisualEditorHistoryStore(
    (s) => s.clearHistory,
  );
  const [pendingNavHref, setPendingNavHref] = useState<string | null>(null);

  // Returns true if navigation was blocked due to unsaved changes
  const hasUnsavedChanges = isEditorMode && visualEditorHistory.length > 0;

  const handleNavGuard = useCallback(
    (href: string): boolean => {
      if (hasUnsavedChanges) {
        setPendingNavHref(href);
        return true;
      }
      return false;
    },
    [hasUnsavedChanges],
  );

  const handleNavClick = useCallback(
    (e: React.MouseEvent, href: string) => {
      if (handleNavGuard(href)) {
        e.preventDefault();
      } else {
        setSidebarOpen(false);
      }
    },
    [handleNavGuard],
  );

  const { isMobile, isMobilePowerEditor } = useResponsive();

  // On mobile, sidebar should always appear expanded (with text labels)
  const isCollapsedView = sidebarCollapsed && !isMobile;
  const { resolvedTheme } = useTheme();
  const { features } = useFeatures();
  const { isPlatformAdmin } = usePlatformAdmin();
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);

  // Filter navigation sections based on feature flags
  const filteredSections = navigationSections.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.featureKey && !features[item.featureKey]) return false;
      if (isEmbedded && item.name === "Templates") return false;
      return true;
    }),
  })).filter((section) => section.items.length > 0);

  // Note: Authentication is handled by ProtectedRoute wrapper
  // No need to check auth here - it creates race conditions with SSO logout

  useEffect(() => {
    // Auto-collapse in editor mode
    if (isEditorMode) {
      setSidebarCollapsed(true);
    } else {
      // Load sidebar state from localStorage for non-editor pages
      const savedState = localStorage.getItem("sidebarCollapsed");
      if (savedState !== null) {
        setSidebarCollapsed(JSON.parse(savedState));
      }
    }
  }, [isEditorMode]);

  // Fetch subscription to determine plan type for upgrade CTA and billing warnings
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = await getIdToken();
        if (token) {
          const sub = await billingApi.getSubscription(token);
          setSubscription(sub);
          setPlanType(sub.planType);
        }
      } catch (error) {
        // Silently fail - don't show upgrade CTA if we can't fetch plan
        console.error("Failed to fetch subscription:", error);
      }
    };
    if (user) {
      fetchSubscription();
    }
  }, [user, getIdToken]);

  const toggleSidebarCollapse = () => {
    // Don't allow manual toggle in editor mode - keep it collapsed
    if (isEditorMode) return;

    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <WorkspaceProvider>
      <OnboardingProvider>
        <PageBarProvider>
        <div className="h-[100dvh] overflow-hidden bg-[hsl(var(--canvas))] flex">
          {/* Mobile sidebar backdrop */}
          {sidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`${
              sidebarOpen ? "translate-x-0" : "-translate-x-full"
            } lg:translate-x-0 fixed inset-y-0 left-0 z-50 w-full sm:w-80 ${
              sidebarCollapsed ? "lg:w-16" : "lg:w-64"
            } bg-[hsl(var(--canvas))] lg:bg-transparent backdrop-blur-xl lg:backdrop-blur-none border-r border-border/50 lg:border-r-0 lg:pt-2 transition-all duration-300 ease-in-out flex flex-col h-[100dvh]`}
          >
            <div className="flex h-[57px] items-center justify-between px-4 lg:px-3">
              {!isEditorMode && isCollapsedView ? (
                /* Collapsed: the logo doubles as the expand trigger. Hovering
                   highlights the whole row and swaps the mark for an expand
                   chevron (Power Editor hover-reveal pattern) — no separate
                   expand-icon row needed. */
                <button
                  onClick={toggleSidebarCollapse}
                  title="Expand sidebar"
                  className="group relative flex w-full items-center justify-center rounded-lg py-1 transition-colors hover:bg-accent"
                >
                  <img
                    src={optidevIcon}
                    alt="OptiDev"
                    className="h-8 w-auto object-contain transition-opacity group-hover:opacity-0"
                  />
                  <ChevronsRight className="absolute h-5 w-5 text-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ) : (
                <Link
                  to="/"
                  onClick={(e) => handleNavClick(e, "/")}
                  className={`flex items-center ${isCollapsedView ? "justify-center w-full" : "gap-2"}`}
                >
                  {isCollapsedView ? (
                    <img
                      src={optidevIcon}
                      alt="OptiDev"
                      className="h-8 w-auto object-contain"
                    />
                  ) : (
                    <img
                      src={
                        resolvedTheme === "dark"
                          ? optidevHorizontalWhite
                          : optidevHorizontal
                      }
                      alt="OptiDev"
                      className="h-8 w-auto object-contain"
                    />
                  )}
                </Link>
              )}
              <div className="flex items-center gap-1">
                {/* Desktop: collapse button (only when expanded) */}
                {!isEditorMode && !isCollapsedView && (
                  <button
                    onClick={toggleSidebarCollapse}
                    className="hidden lg:flex text-muted-foreground hover:text-foreground hover:bg-accent p-1.5 rounded-lg transition flex-shrink-0"
                    title="Collapse sidebar"
                  >
                    <ChevronsLeft className="h-5 w-5" />
                  </button>
                )}
                {/* Mobile: close button */}
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden text-muted-foreground hover:text-foreground hover:bg-accent p-1.5 rounded-lg transition"
                  title="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>


            {/* Workspace Selector */}
            <div className="px-4 lg:px-3 py-2.5 border-b border-foreground/[0.06]">
              <WorkspaceSelector
                collapsed={isCollapsedView}
                onNavGuard={handleNavGuard}
              />
            </div>

            <nav className="flex-1 mt-2 px-4 lg:px-3 overflow-y-auto">
              {filteredSections.map((section, sectionIdx) => (
                <div key={section.label || `section-${sectionIdx}`} className={sectionIdx > 0 ? "mt-3 pt-3 border-t border-foreground/[0.06]" : ""}>
                  {section.label && !isCollapsedView && (
                    <div className="px-4 lg:px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                      {section.label}
                    </div>
                  )}
                  {section.items.map((item) => {
                    const isActive = pathname === item.href;
                    const linkContent = item.external ? (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-muted-foreground hover:bg-muted/50 dark:hover:bg-muted/30 hover:text-foreground border border-transparent group flex items-center ${
                          isCollapsedView ? "justify-center px-0" : "px-4 lg:px-3"
                        } py-3 lg:py-2 mb-0.5 text-base lg:text-sm rounded-md transition-all`}
                      >
                        <item.icon
                          className={`${isCollapsedView ? "" : "mr-4 lg:mr-3"} h-6 w-6 lg:h-5 lg:w-5 flex-shrink-0`}
                        />
                        {!isCollapsedView && item.name}
                      </a>
                    ) : (
                      <AppLink
                        key={item.name}
                        to={item.href}
                        onClick={(e) => handleNavClick(e, item.href)}
                        className={`${
                          isActive
                            ? "bg-card text-foreground font-medium border border-border dark:bg-foreground/[0.12] dark:border-transparent"
                            : "text-muted-foreground hover:bg-accent/50 dark:hover:bg-white/[0.04] hover:text-foreground border border-transparent"
                        } group flex items-center ${
                          isCollapsedView ? "justify-center px-0" : "px-4 lg:px-3"
                        } py-3 lg:py-2 mb-0.5 text-base lg:text-sm rounded-md transition-all`}
                      >
                        <item.icon
                          className={`${
                            isCollapsedView ? "" : "mr-4 lg:mr-3"
                          } h-6 w-6 lg:h-5 lg:w-5 flex-shrink-0 ${
                            ""
                          }`}
                        />
                        {!isCollapsedView && item.name}
                      </AppLink>
                    );

                    return isCollapsedView ? (
                      <Hint
                        key={item.name}
                        text={item.name}
                        side="right"
                        align="center"
                        delayDuration={100}
                      >
                        {linkContent}
                      </Hint>
                    ) : (
                      linkContent
                    );
                  })}
                  {section.label === "Resources" && (
                    <>
                      <div className={`my-2 border-t border-foreground/[0.06] ${isCollapsedView ? "mx-2" : "mx-3"}`} />
                      <ContactSupportNavItem collapsed={isCollapsedView} />
                    </>
                  )}
                </div>
              ))}

              {/* Admin section (platform admins only) */}
              {isPlatformAdmin && (
                <>
                  <div className="mt-4 mb-2 px-2">
                    <div className="border-t border-foreground/[0.06]" />
                  </div>
                  {!isCollapsedView && (
                    <div className="px-4 lg:px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                      Admin
                    </div>
                  )}
                  {(() => {
                    const adminHref = "/dashboard/admin/campaigns";
                    const isAdminActive = pathname.startsWith(adminHref);
                    const linkContent = (
                      <AppLink
                        to={adminHref}
                        onClick={(e) => handleNavClick(e, adminHref)}
                        className={`${
                          isAdminActive
                            ? "bg-card text-foreground font-medium border border-border dark:bg-foreground/[0.12] dark:border-transparent"
                            : "text-muted-foreground hover:bg-accent/50 dark:hover:bg-white/[0.04] hover:text-foreground border border-transparent"
                        } group flex items-center ${
                          isCollapsedView ? "justify-center px-0" : "px-4 lg:px-3"
                        } py-3 lg:py-2 mb-0.5 text-base lg:text-sm rounded-md transition-all`}
                      >
                        <Shield
                          className={`${
                            isCollapsedView ? "" : "mr-4 lg:mr-3"
                          } h-6 w-6 lg:h-5 lg:w-5 flex-shrink-0 ${
                            isAdminActive ? "text-foreground" : ""
                          }`}
                        />
                        {!isCollapsedView && "Campaigns"}
                      </AppLink>
                    );
                    return isCollapsedView ? (
                      <Hint text="Campaigns" side="right" align="center" delayDuration={100}>
                        {linkContent}
                      </Hint>
                    ) : (
                      linkContent
                    );
                  })()}
                  {(() => {
                    const cfgHref = "/dashboard/admin/app-config";
                    const isCfgActive = pathname.startsWith(cfgHref);
                    const linkContent = (
                      <AppLink
                        to={cfgHref}
                        onClick={(e) => handleNavClick(e, cfgHref)}
                        className={`${
                          isCfgActive
                            ? "bg-card text-foreground font-medium border border-border dark:bg-foreground/[0.12] dark:border-transparent"
                            : "text-muted-foreground hover:bg-accent/50 dark:hover:bg-white/[0.04] hover:text-foreground border border-transparent"
                        } group flex items-center ${
                          isCollapsedView ? "justify-center px-0" : "px-4 lg:px-3"
                        } py-3 lg:py-2 mb-0.5 text-base lg:text-sm rounded-md transition-all`}
                      >
                        <Megaphone
                          className={`${
                            isCollapsedView ? "" : "mr-4 lg:mr-3"
                          } h-6 w-6 lg:h-5 lg:w-5 flex-shrink-0 ${
                            isCfgActive ? "text-foreground" : ""
                          }`}
                        />
                        {!isCollapsedView && "App Config"}
                      </AppLink>
                    );
                    return isCollapsedView ? (
                      <Hint text="App Config" side="right" align="center" delayDuration={100}>
                        {linkContent}
                      </Hint>
                    ) : (
                      linkContent
                    );
                  })()}
                </>
              )}
            </nav>

            {/* Upgrade CTA for Free users */}
            {planType === PlanType.FREE && (
              <div className="px-4 lg:px-3 mt-auto mb-2">
                {isCollapsedView ? (
                  <Hint
                    text="Upgrade to Standard"
                    side="right"
                    delayDuration={100}
                  >
                    <AppLink
                      to="/dashboard/settings/billing"
                      onClick={(e) =>
                        handleNavClick(e, "/dashboard/settings/billing")
                      }
                      className="flex items-center justify-center p-2 rounded-lg bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-all"
                    >
                      <Zap className="h-5 w-5 text-purple-500" />
                    </AppLink>
                  </Hint>
                ) : (
                  <AppLink
                    to="/dashboard/settings/billing"
                    onClick={(e) =>
                      handleNavClick(e, "/dashboard/settings/billing")
                    }
                    className="flex items-center gap-4 lg:gap-3 p-4 lg:p-3 rounded-xl lg:rounded-lg bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-all"
                  >
                    <Zap className="h-6 w-6 lg:h-5 lg:w-5 text-purple-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-base lg:text-sm font-medium text-foreground">
                        Upgrade to Standard
                      </p>
                      <p className="text-sm lg:text-xs text-muted-foreground">
                        Unlock better AI models
                      </p>
                    </div>
                  </AppLink>
                )}
              </div>
            )}

            {/* User Section */}
            <div className="flex-shrink-0 p-4 lg:p-3 border-t border-foreground/[0.06]">
              {!isCollapsedView ? (
                // Expanded view
                <div className="flex items-center justify-between">
                  <AppLink
                    to="/dashboard/settings"
                    onClick={(e) => handleNavClick(e, "/dashboard/settings")}
                    className="flex items-center min-w-0 hover:bg-accent/50 rounded-xl lg:rounded-lg p-2 lg:p-1 -m-1 transition-colors"
                  >
                    <div className="h-10 w-10 lg:h-8 lg:w-8 flex items-center justify-center">
                      <Settings className="h-6 w-6 lg:h-5 lg:w-5 text-muted-foreground" />
                    </div>
                    <div className="ml-3 lg:ml-2 min-w-0 flex-1">
                      <p className="text-base lg:text-sm font-medium text-foreground truncate">
                        Settings
                      </p>
                    </div>
                  </AppLink>
                  <div className="flex items-center gap-2 lg:gap-1">
                    {!isEmbedded && <ThemeSwitcherPopover />}
                    {!isEmbedded && (
                      <button
                        onClick={signOut}
                        className="text-muted-foreground hover:text-destructive transition-colors p-2.5 lg:p-1.5"
                        title="Sign Out"
                      >
                        <LogOut className="h-5 w-5 lg:h-4 lg:w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                // Collapsed view (desktop only)
                <div className="space-y-2">
                  <Hint text="Settings" side="right" delayDuration={100}>
                    <AppLink
                      to="/dashboard/settings"
                      onClick={(e) => handleNavClick(e, "/dashboard/settings")}
                      className="h-8 w-8 flex items-center justify-center mx-auto hover:bg-accent/50 rounded-lg transition-colors"
                    >
                      <Settings className="h-5 w-5 text-muted-foreground" />
                    </AppLink>
                  </Hint>
                  <div className="flex flex-col gap-1">
                    {!isEmbedded && (
                      <Hint
                        text="Toggle theme"
                        side="right"
                        delayDuration={100}
                      >
                        <div>
                          <ThemeSwitcherPopover collapsed />
                        </div>
                      </Hint>
                    )}
                    {!isEmbedded && (
                      <Hint text="Sign Out" side="right" delayDuration={100}>
                        <button
                          onClick={signOut}
                          className="w-full flex justify-center text-muted-foreground hover:text-destructive transition-colors p-1.5"
                        >
                          <LogOut className="h-4 w-4" />
                        </button>
                      </Hint>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Content region — flat canvas holding the top banner + the content
              card panel (sidebar + chrome have no background). Desktop only;
              mobile stays full-bleed. */}
          <div
            className={`flex-1 min-w-0 flex flex-col min-h-0 ${
              sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
            } lg:gap-2 lg:py-2 lg:pr-2 transition-all duration-300`}
          >
            {/* Global top strip removed — the in-content page header (title +
                description + actions) is the top element on desktop now. Restore
                by re-adding <GlobalStrip /> here (component still exists). */}

            {/* Mobile header */}
            {!isMobilePowerEditor && (
              <header className="lg:hidden sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-foreground/[0.06]">
                <div className="px-4">
                  <div className="flex h-14 items-center justify-between">
                    <button
                      onClick={() => setSidebarOpen(true)}
                      className="text-muted-foreground hover:text-foreground p-1 -ml-1"
                    >
                      <Menu className="h-6 w-6" />
                    </button>
                    {/* Centered logo */}
                    <Link
                      to="/dashboard"
                      onClick={(e) => handleNavClick(e, "/dashboard")}
                      className="absolute left-1/2 -translate-x-1/2"
                    >
                      <img
                        src={
                          resolvedTheme === "dark"
                            ? optidevHorizontalWhite
                            : optidevHorizontal
                        }
                        alt="OptiDev"
                        className="h-7 w-auto object-contain"
                      />
                    </Link>
                    {/* Spacer for balance */}
                    <div className="w-6" />
                  </div>
                </div>
              </header>
            )}

            {/* Attention area — on the flat canvas, above the content panel
                (outside the card). `empty:hidden` collapses it when none render. */}
            <div className="flex flex-col gap-2 empty:hidden sm:px-6 lg:px-px [&>*]:overflow-hidden sm:[&>*]:rounded-lg">
              {/* Maintenance banner */}
              <MaintenanceBanner />

              {/* Global announcement banner (DB-driven, see app_configs.config.announcement) */}
              <AnnouncementBanner />

              {/* Workspace crash-recovery notice (project-scoped, published by
                  the PowerEditor route via useRecoveryNoticeStore) */}
              <WorkspaceRecoveryBanner />

              {/* Billing warning banner */}
              <BillingWarningBanner subscription={subscription} />

              {/* Payment-method handover bar (new owner, non-embed) */}
              <PmHandoverBar />
            </div>

            {/* Content panel — bordered surface floating on the flat canvas. The
                dotted texture lives HERE (a non-scrolling ambient layer) rather
                than on <main>; an inset:0 pattern on a scroll container only
                covers the first viewport, leaving the rest solid on scroll. */}
            <div className="flex min-h-0 flex-1 flex-col dotted-background-interactive dotted-on-card lg:overflow-hidden lg:rounded-2xl lg:border lg:border-border/60 lg:bg-[hsl(var(--panel))]">
              <PageBar />

              {/* Page content — scrolls over the panel's ambient dotted texture */}
              <main className="flex-1 overflow-y-auto overflow-x-hidden relative bg-transparent">
                <Outlet />
              </main>
            </div>
          </div>

          {/* Referral bonus popup for new users from influencer campaigns - mounted at layout level so it survives navigation from /dashboard to /dashboard/editor/:id triggered by autoSubmit flow */}
          <ReferralBonusModal />
          <RemixIntentHandler />

          {/* Unsaved visual editor changes dialog (sidebar navigation) */}
          <UnsavedChangesDialog
            open={!!pendingNavHref}
            onCancel={() => setPendingNavHref(null)}
            onDiscard={() => {
              clearVisualEditorHistory();
              if (pendingNavHref) {
                navigate(pendingNavHref);
              }
              setPendingNavHref(null);
            }}
          />
        </div>
        </PageBarProvider>
      </OnboardingProvider>
    </WorkspaceProvider>
  );
}
